import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export type ActivityType =
  | 'call_outbound'
  | 'call_inbound'
  | 'meeting_in_person'
  | 'meeting_video'
  | 'email_sent'
  | 'sms_sent'
  | 'note'
  | 'document_sent'
  | 'other';

export type ActivityTypeFilter = ActivityType | 'all';

export interface ActivityRecord {
  activityId: string;
  companyId: string;
  brokerId: string | null;
  brokerName: string | null;
  activityType: string;
  outcome: string | null;
  clientName: string | null;
  clientEmail: string | null;
  clientPhone: string | null;
  linkedLoanId: string | null;
  linkedLeadId: string | null;
  notes: string | null;
  durationSeconds: number | null;
  followUpAt: string | null;
  occurredAt: string | null;
  loggedAt: string | null;
}

export interface LogActivityArgs {
  activityType: ActivityType;
  outcome?: string;
  clientName?: string;
  clientEmail?: string | null;
  clientPhone?: string | null;
  linkedLoanId?: string;
  linkedLeadId?: string;
  notes?: string;
  durationSeconds?: number;
  followUpAt?: string;
  occurredAt?: string;
}

interface UseActivitiesState {
  activities: ActivityRecord[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  mineOnly: boolean;
  setMineOnly: (v: boolean) => void;
  activityTypeFilter: ActivityTypeFilter;
  setActivityTypeFilter: (v: ActivityTypeFilter) => void;
  sinceDaysAgo: number;
  setSinceDaysAgo: (v: number) => void;
  logActivity: (args: LogActivityArgs) => Promise<string>;
  deleteActivity: (activityId: string) => Promise<void>;
}

export function useActivities(companyId: string | undefined): UseActivitiesState {
  const [activities, setActivities] = useState<ActivityRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState<number>(0);
  const [mineOnly, setMineOnly] = useState<boolean>(true);
  const [activityTypeFilter, setActivityTypeFilter] = useState<ActivityTypeFilter>('all');
  const [sinceDaysAgo, setSinceDaysAgo] = useState<number>(30);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    const fn = httpsCallable<
      {
        action: 'listActivities';
        companyId: string;
        mineOnly: boolean;
        activityType: ActivityTypeFilter;
        sinceDaysAgo: number;
        limit: number;
      },
      { activities: ActivityRecord[] }
    >(functions, 'activityAction');

    setLoading(true);
    setError(null);
    fn({
      action: 'listActivities',
      companyId,
      mineOnly,
      activityType: activityTypeFilter,
      sinceDaysAgo,
      limit: 300,
    })
      .then((r) => setActivities(r.data.activities))
      .catch((err) => {
        console.error('[useActivities] fetch failed', err);
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => setLoading(false));
  }, [companyId, mineOnly, activityTypeFilter, sinceDaysAgo, refreshKey]);

  const logActivity = useCallback(
    async (args: LogActivityArgs) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        { action: 'logActivity'; companyId: string } & LogActivityArgs,
        { success: boolean; activityId: string }
      >(functions, 'activityAction');
      const result = await fn({ action: 'logActivity', companyId, ...args });
      setRefreshKey((k) => k + 1);
      return result.data.activityId;
    },
    [companyId]
  );

  const deleteActivity = useCallback(
    async (activityId: string) => {
      if (!companyId) throw new Error('companyId required');
      const fn = httpsCallable<
        { action: 'deleteActivity'; companyId: string; activityId: string },
        { success: boolean }
      >(functions, 'activityAction');
      await fn({ action: 'deleteActivity', companyId, activityId });
      setRefreshKey((k) => k + 1);
    },
    [companyId]
  );

  return {
    activities,
    loading,
    error,
    refresh,
    mineOnly,
    setMineOnly,
    activityTypeFilter,
    setActivityTypeFilter,
    sinceDaysAgo,
    setSinceDaysAgo,
    logActivity,
    deleteActivity,
  };
}
